const { getServerHost } = require("../util/host.js");

/**
 * Client for handling Server-Sent Events (SSE) communication with the server.
 * Only one SSE connection exists.
 */
class EventClient {
  /** Singleton instance of the EventClient */
  static instance = null;

  /**
   * Private constructor to prevent direct instantiation.
   */
  constructor() {
    /** Maximum number of reconnection attempts before giving up */
    this.MAX_RECONNECT_ATTEMPTS = 5;

    /** Base URL for the SSE endpoint */
    this.EVENT_SOURCE_URL = `${getServerHost()}/api/events`;

    /** Active EventSource connection */
    this.eventSource = null;

    /** Map of event types to their registered callback functions */
    this.eventListeners = new Map();

    /** Map of event types to their server event handlers */
    this.eventHandlers = new Map();

    /** Counter for connection retry attempts */
    this.reconnectAttempts = 0;
  }

  /**
   * Gets the singleton instance of EventClient
   * @returns {EventClient} The EventClient instance
   */
  static getInstance() {
    if (!EventClient.instance) {
      EventClient.instance = new EventClient();
    }

    return EventClient.instance;
  }

  /**
   * Establishes SSE connection if not already connected.
   * Does nothing if a connection already exists.
   */
  connect() {
    if (this.eventSource) {
      return;
    }

    this.initializeEventSource();
  }

  /**
   * Disconnects SSE connection and cleans up resources.
   * Safe to call even if no connection exists.
   */
  disconnect() {
    if (this.eventSource) {
      this.cleanup();
    }
  }

  /**
   * Subscribe to a specific event type with a callback function.
   * If this is the first subscription for this event type, sets up the server event listener.
   * @param {string} type - The event type to subscribe to
   * @param {Function} callback - The callback function to execute when event is received
   */
  subscribe(type, callback) {
    if (!this.eventListeners.has(type)) {
      this.eventListeners.set(type, new Set());
      this.addServerEventListener(type);
    }

    this.eventListeners.get(type).add(callback);
  }

  /**
   * Unsubscribes from all event types and removes all callbacks.
   */
  unsubscribeAll() {
    for (const type of this.eventListeners.keys()) {
      this.unsubscribe(type);
    }
  }

  /**
   * Unsubscribes one callback for a given event type if provided,
   * otherwise removes all subscriptions for that event type.
   * @param {string} type - The event type to unsubscribe from
   * @param {Function} [callback] - The specific callback to remove.
   */
  unsubscribe(type, callback) {
    if (callback) {
      const listeners = this.eventListeners.get(type);
      if (listeners) {
        listeners.delete(callback);

        if (listeners.size === 0) {
          this.eventListeners.delete(type);
          this.removeServerEventListener(type);
        }
      }
    } else {
      // Remove all subscriptions for this event:
      if (this.eventListeners.has(type)) {
        this.eventListeners.delete(type);
        this.removeServerEventListener(type);
      }
    }
  }

  /**
   * Returns a record of all current event subscriptions and their callback counts.
   * @returns {Object} An object mapping each event type to the number of active callbacks
   */
  getSubscriptions() {
    const subscriptions = {};
    for (const [type, callbacks] of this.eventListeners.entries()) {
      subscriptions[type] = callbacks.size;
    }
    return subscriptions;
  }

  /**
   * Initializes a new EventSource connection and sets up base event handlers.
   * Includes automatic reconnection logic on connection errors.
   */
  initializeEventSource() {
    this.eventSource = new EventSource(this.EVENT_SOURCE_URL);

    this.eventSource.onopen = () => {
      this.reconnectAttempts = 0;

      // Reattach all existing listeners after reconnection
      for (const [type] of this.eventListeners) {
        this.addServerEventListener(type);
      }
    };

    this.eventSource.onerror = () => {
      this.handleError();
    };
  }

  /**
   * Handles connection errors by implementing exponential backoff retry logic.
   * Will attempt to reconnect up to MAX_RECONNECT_ATTEMPTS times.
   */
  handleError() {
    if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      this.reconnectAttempts += 1;
      setTimeout(() => {
        this.cleanup();
        this.initializeEventSource();
      }, 1000 * this.reconnectAttempts);
    }
  }

  /**
   * Cleans up the existing EventSource connection.
   */
  cleanup() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Sets up an event listener for a specific event type on the EventSource.
   * Handles JSON parsing of event data and distributes to all registered callbacks.
   * @param {string} type - The event type to listen for.
   */
  addServerEventListener(type) {
    if (!this.eventSource) {
      return;
    }

    const handler = (event) => {
      // Try to parse JSON data, fall back to raw data if parsing fails
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        data = event.data;
      }

      // Distribute event to all registered callbacks
      const callbacks = this.eventListeners.get(type);
      if (callbacks) {
        for (const callback of callbacks) {
          callback(data);
        }
      }
    };

    this.eventHandlers.set(type, handler);
    this.eventSource.addEventListener(type, handler);
  }

  /**
   * Removes an event listener for a specific event type from the EventSource.
   * @param {string} type - The event type to remove the listener for.
   */
  removeServerEventListener(type) {
    if (!this.eventSource) {
      return;
    }

    const handler = this.eventHandlers.get(type);

    if (handler) {
      this.eventSource.removeEventListener(type, handler);
      this.eventHandlers.delete(type);
    }
  }
}

module.exports = EventClient.getInstance();
